import { useMemo } from "react";
import { get, isEmpty, isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import CanvasIcon from "./CanvasIcon";
import { isInputStep } from "./next-steps";
import QuestionsSection from "./QuestionsSection";
import ScreenShareMenu from "./ScreenShareMenu";
import ScreenWorkshop from "./ScreenWorkshop";
import { ICON_PATHS } from "./step-look";
import { SCREEN_STATES, screenState } from "./step-summary";
import { screenVersions, STEP_TABS } from "./step-tabs";

const SHOWN_STATES = [SCREEN_STATES.PUBLISHED, SCREEN_STATES.SHARED];

const SCREEN_TABS = [STEP_TABS.MAIN, STEP_TABS.FIELDS];

const ScreenTab = ({
  node,
  nodes,
  tab,
  persisted,
  processId,
  variables,
  liveFormDefinition,
  onChange,
  onFieldsOpen,
  reload,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const STATUSES = t("FORM.STATUSES", { returnObjects: true }) || {};
  const SCREEN = t("CANVAS_EDITOR.STEP.SCREEN", { returnObjects: true });
  const QUESTIONS = t("CANVAS_EDITOR.STEP.QUESTIONS", { returnObjects: true });

  const data = node.data || {};
  const statusId = node.id;
  const onScreen = SCREEN_TABS.includes(tab);
  const binding = !isNil(data.screenVersionId);
  const state = screenState(data);
  const shown = SHOWN_STATES.includes(state);
  const borrowed = !binding && state === SCREEN_STATES.SHARED;
  const hasQuestions = !isEmpty(get(data, "formDefinition.fields"));
  const screenFrom = get(data, "config.screenFrom") || null;
  const versions = screenVersions(data);
  const draftOverLive = !isNil(versions.draft) && !isNil(versions.published);
  const publishedSlots = data.screenPublishedSlots;
  const publishedStates = data.screenPublishedStates;
  const publishedScreen = useMemo(
    () =>
      draftOverLive
        ? { slots: publishedSlots || [], states: publishedStates || [] }
        : null,
    [draftOverLive, publishedSlots, publishedStates],
  );

  const handleScreenFromChange = (value) => {
    const config = { ...(data.config || {}) };
    if (value) config.screenFrom = value;
    else delete config.screenFrom;
    onChange("config", config);
  };

  const share = {
    labels: {
      TITLE: SCREEN.SHARE,
      PLACEHOLDER: SCREEN.SHARE_PLACEHOLDER,
      HINT: SCREEN.SHARE_HINT,
      IGNORED: STATUSES.SHARED_SCREEN_IGNORED,
    },
    options: nodes
      .filter(
        (item) => item.id !== statusId && !isNil(item.data.screenVersionId),
      )
      .map((item) => ({ value: item.id, label: item.data.name || item.id })),
    value: screenFrom,
    ignored: binding && !isNil(screenFrom),
    onChange: handleScreenFromChange,
  };

  return (
    <>
      {onScreen && !shown ? (
        <div className="canvas-alert canvas-alert--warning canvas-workspace__notice">
          <CanvasIcon path={ICON_PATHS.info} />
          <span>
            {isInputStep(node, nodes) && hasQuestions
              ? SCREEN.QUESTIONS_INSTEAD
              : SCREEN.PASS_THROUGH}
          </span>
        </div>
      ) : null}
      {onScreen && borrowed ? (
        <div className="canvas-versions">
          <div className="canvas-version canvas-version--ok">
            <span className="canvas-version__dot" aria-hidden="true" />
            <div className="canvas-version__text">
              <strong>
                {t("CANVAS_EDITOR.STEP.SCREEN.SHARED", {
                  name: data.screenFromName,
                })}
              </strong>
              <span>{SCREEN.SHARED_HINT}</span>
            </div>
            <ScreenShareMenu share={share} withLabel />
          </div>
        </div>
      ) : null}
      {onScreen && borrowed ? null : (
        <ScreenWorkshop
          tab={tab}
          fieldsOpen={tab === STEP_TABS.FIELDS}
          onFieldsOpen={onFieldsOpen}
          share={share}
          processId={processId}
          title={data.name}
          screenVersionId={data.screenVersionId}
          statusId={statusId}
          screenId={data.screenId}
          versionNumber={data.screenVersionNumber}
          htmlContent={data.screenHtml || ""}
          styleContent={data.screenStyleContent || ""}
          slots={data.screenSlots}
          report={data.screenReport}
          states={data.screenStates}
          variables={variables}
          formDefinition={data.formDefinition}
          liveFormDefinition={liveFormDefinition}
          publishedScreen={publishedScreen}
          published={data.screenVersionStatus === "published"}
          draftOnly={!persisted}
          onFormDefinitionChange={(next) => onChange("formDefinition", next)}
          reload={reload}
        />
      )}
      {onScreen && !binding ? (
        <div className="canvas-tab">
          <p className="canvas-tab__hint">{QUESTIONS.SCREEN_HINT}</p>
          <QuestionsSection
            formDefinition={data.formDefinition}
            variables={variables}
            title={QUESTIONS.SCREEN_TITLE}
            onChange={(next) => onChange("formDefinition", next)}
          />
        </div>
      ) : null}
    </>
  );
};

ScreenTab.propTypes = {
  node: PropTypes.object.isRequired,
  nodes: PropTypes.arrayOf(PropTypes.object),
  tab: PropTypes.string,
  persisted: PropTypes.bool,
  processId: PropTypes.string,
  variables: PropTypes.array,
  liveFormDefinition: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onFieldsOpen: PropTypes.func,
  reload: PropTypes.func,
};

ScreenTab.defaultProps = {
  nodes: [],
  tab: STEP_TABS.MAIN,
  persisted: false,
  processId: null,
  variables: [],
  liveFormDefinition: undefined,
  onFieldsOpen: () => undefined,
  reload: null,
};

export default ScreenTab;
